import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useCookies } from "react-cookie";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import axiosInstance from "../../axiosInstance";
import { Link } from "@mui/material";
import SanitizeTextboxInput from "../../components/SiteSettings/SanitizeTextboxInput";
import { useEffect } from "react";

type SiteItemProps = {
  data: any;
  sitesData: any[];
  onSiteDelete: (siteId: string) => void;
  onSiteRename: (siteId: string, newName: string) => void;
  onSiteDuplicate: (siteId: string) => void;
  onEditSite: () => void;
  onSettingClick: () => void;
  onMoreOptionClick: () => void;
  onPublishComplete?: (siteId: string) => void;
};

const SiteItem: React.FC<SiteItemProps> = (props) => {
  // --- DEBUG HELPERS ---
  const __DBG__ = (msg: string, data?: any) => {
    // Toggle verbose logs by setting window.__RP_DEBUG__ = true in DevTools console
    const enabled = typeof window !== "undefined" && (window as any).__RP_DEBUG__ === true;
    if (!enabled) return;
    try {
      // eslint-disable-next-line no-console
      console.log(`[SiteItem] ${msg}`, data ?? "");
    } catch {
      // ignore
    }
  };

  __DBG__("render", { siteId: props?.data?.siteId, pageId: props?.data?.pageId, siteUrl: props?.data?.siteUrl });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const publishingSnackKeyRef = React.useRef<any>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] =
    React.useState(false);
  const [showRename, setShowRename] = React.useState(false);
  const [renameError, setRenameError] = React.useState("");
  const [newSiteName, setNewSiteName] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [cookie_userData, setCookie_userData, removeCookie_userData] =
    useCookies(["rp_user_data"]);

// --- Publish status polling (singleton) ---
const publishPollRef = React.useRef<{
  intervalId: ReturnType<typeof setInterval> | null;
  timeoutId: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
}>({ intervalId: null, timeoutId: null, inFlight: false });

const stopPublishPolling = React.useCallback(() => {
  // Stop any active publish status polling
  __DBG__("stopPublishPolling");
  if (publishPollRef.current.intervalId) {
    clearInterval(publishPollRef.current.intervalId);
  }
  if (publishPollRef.current.timeoutId) {
    clearTimeout(publishPollRef.current.timeoutId);
  }
  publishPollRef.current.intervalId = null;
  publishPollRef.current.timeoutId = null;
  publishPollRef.current.inFlight = false;
}, []);

useEffect(() => {
  return () => {
    // Cleanup on unmount
    stopPublishPolling();
  };
}, [stopPublishPolling]);

const normalizeStatus = (raw: any) => {
  const s =
    raw?.status ??
    raw?.publishStatus ??
    raw?.pageStatus ??
    raw?.publishingStatus ??
    raw;
  const out = String(s || "").trim().toLowerCase();
  __DBG__("normalizeStatus", { raw, out });
  return out;
};


  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [textClassObj] = React.useState([
    { fontSize: "14px", color: "#243060", minHeight: "auto" },
    {
      "&:hover": {
        fontWeight: 700,
      },
    },
  ]);

  function calculateTimeRemaining(timestamp: number): string {
    // Convert the timestamp to a Date object
    const originalDate = new Date(timestamp);

    // Add 72 hours (72 * 60 * 60 * 1000 milliseconds)
    const futureDate = new Date(originalDate.getTime() + 72 * 60 * 60 * 1000);

    // Get the current time
    const currentDate = new Date();

    // Calculate the difference in milliseconds
    const diffMs = futureDate.getTime() - currentDate.getTime();

    // If the future date has passed, return that it's already deleted
    if (diffMs <= 0) {
      return "The site will be deleted soon.";
    }

    // Calculate the difference in days and hours
    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    const diffHours = Math.floor(
      (diffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
    );

    // Return the remaining time in a readable format
    if (diffDays > 0) {
      return `Update or save with in ${diffDays} day(s) to avoid deletion.`;
    } else {
      return `Update or save with in ${diffHours} hour(s) to avoid deletion.`;
    }
  }

  const handleDelete = () => {
    // onCancelSubs(existingSubs[0].subscriptionId); // Call the delete function passed as a prop
    setShowConfirmation(false); // Close the confirmation dialog after delete
    props.onSiteDelete(props.data.siteId);
  };

  const publishSite = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/api/configs/publish", {
        siteId: props.data.siteId,
        fullSite: true,
        pageId: null,
        publishAllPages: true,
      });
      __DBG__("publish API success", { status: (response as any)?.status, data: (response as any)?.data });
      setIsLoading(false);
      publishingSnackKeyRef.current = enqueueSnackbar(
        "Your website is being published. We'll notify you when it's ready.",
        {
          variant: "success",
          className: "bg-catelina-blue",
          persist: true,
        }
      );
    if (typeof (window as any).__RP_DEBUG__ !== "undefined" && (window as any).__RP_DEBUG__) {
      console.log("[publish] starting status poll", {
        siteId: props?.data?.siteId,
        pageId: props?.data?.pageId,
        siteUrl: props?.data?.siteUrl,
        });
        enqueueSnackbar("DEBUG: started publish polling", { variant: "info" });
    }
      checkPublishStatus();

      return response;
    } catch (error) {
      console.error("Error while publishing site:", error);
      __DBG__("publish API error", error);
      setIsLoading(false);
    }
  };

  const onPublishViewClick = async () => {
    __DBG__("onPublishViewClick", { isPublished: props?.data?.isPublished, isPublishing });
    setShowPublishConfirmation(false);
    if (!props.data.isPublished) {
      setIsPublishing(true);
      const resp = await publishSite();
      // if (!!resp?.data?.data?.url) {
      //   window.open(`https://${resp?.data?.data?.url}`, '_blank')?.focus();
      // } else {
      //   console.log('Error while publishing site');
      // }
    } else {
      window.open(props.data.siteUrl, "_blank")?.focus();
    }
  };

    const getPublishStatus = async () => {
    try {
      const siteId = props?.data?.siteId;
      const pageId = props?.data?.pageId;

      if (!pageId && !siteId) {
        console.error("getPublishStatus: missing pageId/siteId", props?.data);
        return null;
      }

    /**
     * Constructs a query string parameter based on the presence of pageId.
     * If pageId exists, creates a pageId query parameter; otherwise, creates a siteId query parameter.
     * Both values are URL-encoded to ensure safe transmission.
     * 
     * @remarks
     * The query string will be in the format "pageId=<encoded-value>" or "siteId=<encoded-value>".
     */
      const qs = pageId
        ? `pageId=${encodeURIComponent(pageId)}`
        : `siteId=${encodeURIComponent(siteId)}`;

      const response = await axiosInstance.get(
        `/api/configs/check-publish-status?${qs}`
      );
      return response.data;
    } catch (error) {
      console.error("Error while getting publish status:", error);
      setIsLoading(false);
      return null;
    }
  };

  const checkSSL = async (url:any) => {
    try {
      const response = await axiosInstance.get(
        `/api/configs/check-ssl?url=${url}`
      );
      return response.status;
    } catch (error) {
      console.error("Error while checking SSL:", error);
      setIsLoading(false);
    }
  };

/**
 * Initiates polling to check the publish status of a site.
 * 
 * Polls the publish status every 5 seconds until the status is either "completed" or "failed",
 * or until a 5-minute timeout is reached. Ensures only one poller instance is active at a time
 * by stopping any existing polling before starting.
 * 
 * @remarks
 * - On successful completion, unblocks the UI, triggers the parent's `onPublishComplete` callback,
 *   and shows a success notification.
 * - For custom domains (not ending with `SERVER_DOMAIN`), initiates a separate SSL readiness check
 *   that polls every 5 seconds up to 60 times (5 minutes).
 * - On failure or timeout, stops polling, unblocks the UI, and displays an error notification.
 * - Uses a ref-based flag (`publishPollRef.current.inFlight`) to prevent concurrent poll requests.
 * 
 * @throws Does not throw directly, but catches and handles exceptions internally by stopping the
 * poller and showing an error notification.
 */
  const checkPublishStatus = () => {
    __DBG__("checkPublishStatus START");
    // Ensure there is only one active poller at a time.
    stopPublishPolling();

    publishPollRef.current.intervalId = setInterval(async () => {
      if (publishPollRef.current.inFlight) return;
      publishPollRef.current.inFlight = true;

      try {
        __DBG__("poll tick", { inFlight: publishPollRef.current.inFlight });
        const publishResponse = await getPublishStatus();
        const envelope = publishResponse?.data ?? publishResponse;
        const raw = envelope?.data ?? envelope;
        const raw2 = raw?.data ?? raw;
        __DBG__("unwrap", { envelopeKeys: envelope && typeof envelope==='object' ? Object.keys(envelope) : null, rawKeys: raw && typeof raw==='object' ? Object.keys(raw) : null, raw2Keys: raw2 && typeof raw2==='object' ? Object.keys(raw2) : null });
        const status = normalizeStatus(raw2);

        if (status === "completed") {
          __DBG__("status=completed");
          stopPublishPolling();

          // Unblock UI immediately
          setIsLoading(false);
          setIsPublishing(false);
          setShowPublishConfirmation(false);

          // Let parent refresh its data so "Published" flips in the list.
          if (typeof props?.onPublishComplete === "function") {
            props.onPublishComplete(props.data.siteId);
          }

          // Show completion immediately (do NOT wait for SSL).
          showPublishNotification(props.data.siteUrl);

          // For custom domains, check SSL readiness separately and notify (optional)
          if (!props.data.siteUrl.endsWith(process.env.SERVER_DOMAIN || '')) {
            let sslTries = 0;
            const sslInterval = setInterval(async () => {
              sslTries += 1;
              const sslResponse = await checkSSL(`${props.data.siteUrl}`);
              if (sslResponse === 200) {
                clearInterval(sslInterval);
                enqueueSnackbar("SSL is ready for your domain.", {
                  variant: "success",
                  className: "bg-catelina-blue",
                });
              } else if (sslResponse === 500 || sslTries >= 60) {
                clearInterval(sslInterval);
                enqueueSnackbar(
                  "Your site is published, but SSL is still being provisioned for the domain.",
                  { variant: "info" }
                );
              }
            }, 5000);
          }
        } else if (status === "failed") {
          __DBG__("status=failed", raw);
          stopPublishPolling();
          setIsLoading(false);
          setIsPublishing(false);
          setShowPublishConfirmation(false);
          showErrorNotification(raw?.publishError || undefined);
        }
      } catch (e) {
        __DBG__("poller exception", e);
        stopPublishPolling();
        setIsLoading(false);
        setIsPublishing(false);
        setShowPublishConfirmation(false);
        showErrorNotification("Failed while checking publish status.");
      } finally {
        publishPollRef.current.inFlight = false;
      }
    }, 5000);

    publishPollRef.current.timeoutId = setTimeout(() => {
      __DBG__("poller timeout");
      if (publishPollRef.current.intervalId) {
        stopPublishPolling();
        setIsLoading(false);
        setIsPublishing(false);
        setShowPublishConfirmation(false);
        showErrorNotification();
      }
    }, 300_000);
  };

/**
 * Displays a success notification when publishing is completed.
 * 
 * The notification includes a link to visit the published website and persists
 * until manually dismissed. This function also handles cleanup of any existing
 * snackbars, including the "publishing..." notification.
 * 
 * @param domain - The URL of the published website. Defaults to an empty string.
 * 
 * @remarks
 * - Sets `isPublishing` state to `false`
 * - Closes all existing snackbars before showing the new one
 * - Clears the `publishingSnackKeyRef` reference
 * - The notification can be dismissed by clicking anywhere on it
 * - The notification uses a persistent variant that won't auto-dismiss
 */
const showPublishNotification = (domain = "") => {
    __DBG__("showPublishNotification", { domain });
    setIsPublishing(false);

    // Close the earlier "publishing..." snackbar (if any)
    if (publishingSnackKeyRef.current) {
      closeSnackbar(publishingSnackKeyRef.current);
      publishingSnackKeyRef.current = null;
    }

    enqueueSnackbar(
      <div
        className="flex flex-col"
        onClick={() => {
          closeSnackbar();
        }}
      >
        <span>Publishing Completed!</span>
        <a
          className="text-white underline"
          href={`${domain}`}
          target="_blank"
          rel="noreferrer"
        >
          Visit website
        </a>
      </div>,
      {
        variant: "success",
        className: "bg-catelina-blue",
        persist: true,
      }
    );
  };

/**
 * Displays an error notification to the user when site publishing fails.
 * 
 * This function performs the following actions:
 * 1. Sets the publishing state to false
 * 2. Logs the error message for debugging
 * 3. Closes the specific "publishing..." snackbar if it exists
 * 4. Displays a new error snackbar with the provided message
 * 
 * @param msg - The error message to display. Defaults to "Something went wrong while publishing your site"
 * @returns void
 */
const showErrorNotification =
 (msg = "Something went wrong while publishing your site") => {
    setIsPublishing(false);
    __DBG__("showErrorNotification", { msg });

    // Close the earlier "publishing..." snackbar (if any)
    if (publishingSnackKeyRef.current) {
      closeSnackbar(publishingSnackKeyRef.current);
      publishingSnackKeyRef.current = null;
    }

    enqueueSnackbar(msg, {
      variant: "error",
    });
  };

/**
 * Handles the site rename operation.
 * Validates the new site name by checking if it's not blank and doesn't already exist
 * (case-insensitive comparison) among other sites.
 * If validation passes, hides the rename dialog and triggers the rename callback.
 * 
 * @remarks
 * - Sets a "Site name can't be blank" error if the new name is empty
 * - Sets an "Entered site name already exists" error if the name conflicts with another site
 * - Excludes the current site from the duplicate name check using siteId comparison
 * 
 * @returns {void}
 */
const handleRename = () => {
    // console.log("rename", props.sitesData, newSiteName);
    if (newSiteName === "") {
      setRenameError("Site name can't be blank");
      return;
    } else {
      let idx = props.sitesData.findIndex(
        (kk) =>
          kk.siteName.toLowerCase() === newSiteName.toLowerCase() &&
          kk.siteId !== props.data.siteId
      );
      if (idx >= 0) {
        setRenameError("Entered site name already exists");
        return;
      }
    }

    setShowRename(false);
    props.onSiteRename(props.data.siteId, newSiteName);
  };

/**
 * Array of menu items for site actions.
 * 
 * @remarks
 * Contains two menu items:
 * 1. Publish & View - Conditionally publishes and/or views the site
 *    - If site is published: navigates to view
 *    - If site is not published and not currently publishing: shows publish confirmation dialog
 *    - If publishing is in progress: shows a warning notification
 * 
 * 2. Rename - Opens the rename dialog for the site
 *    - Disabled for site with ID "2e2b85c5-1b76-4e74-8e39-c82749386c53"
 *    - Resets the new site name state before showing the dialog
 * 
 * @see {@link onPublishViewClick} - Handler for viewing published sites
 * @see {@link setShowPublishConfirmation} - State setter for publish confirmation dialog
 * @see {@link setShowRename} - State setter for rename dialog
 */
  const menuItems = [
    <MenuItem
      key="publish-view"
      onClick={() => {
        if (props.data.isPublished) {
          onPublishViewClick();
        } else {
          if (isPublishing) {
            enqueueSnackbar("Publishing already in progress!", {
              variant: "warning",
            });
            return;
          }
          setShowPublishConfirmation(true);
        }
      }}
      sx={textClassObj}
    >
      <ListItemIcon>
        <VisibilityOutlinedIcon sx={textClassObj} />
      </ListItemIcon>
      {!props.data.isPublished && "Publish & "}View
    </MenuItem>,
    <MenuItem
      key="rename"
      onClick={() => {
        setNewSiteName("");
        setShowRename(true);
      }}
      sx={textClassObj}
      disabled={props.data.siteId === "2e2b85c5-1b76-4e74-8e39-c82749386c53"}
    >
      <ListItemIcon>
        <DriveFileRenameOutlineOutlinedIcon
          fontSize="small"
          sx={textClassObj}
        />
      </ListItemIcon>
      Rename
    </MenuItem>,
  ];

  if (
    cookie_userData?.rp_user_data?.userId &&
    props.data.siteOwnerUserId === cookie_userData.rp_user_data.userId
  ) {
    menuItems.push(
      <MenuItem
        key="duplicate"
        onClick={() => {
          props.onSiteDuplicate(props.data.siteId);
        }}
        sx={textClassObj}
      >
        <ListItemIcon>
          <ContentCopyRoundedIcon sx={textClassObj} />
        </ListItemIcon>
        Duplicate
      </MenuItem>,
      <MenuItem
        key="delete"
        onClick={() => setShowConfirmation(true)}
        sx={[...textClassObj, { color: "#F00" }]}
        disabled={
          props.data.isTemplate === "true"
           || props.data.siteId === "2e2b85c5-1b76-4e74-8e39-c82749386c53"
        }
      >
        <ListItemIcon>
          <DeleteOutlineOutlinedIcon
            sx={[...textClassObj, { color: "#F00" }]}
          />
        </ListItemIcon>
        Delete
      </MenuItem>
    );
  }
  (props.data.isDirty || props.data.disabled);
  return (
    <Card
      className={`relative site-card ${
        props.data.isDirty === "true" ? "bg-[#FFF3F6]" : "bg-white"
      }`}
    >
      {showConfirmation && (
        <div className="fixed inset-0 overflow-y-auto flex justify-center items-center z-10">
          <div className="fixed inset-0">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <div className="bg-white rounded card-delete p-8 max-w-md w-11/12 md:w-full mx-auto z-50">
            <p className="text-gray-800 mb-4">
              Are you sure you want to delete this website?
            </p>
            <div className="flex justify-end flex-col sm:flex-row">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2 focus:outline-none focus:shadow-outline w-full sm:w-auto mb-2.5 sm:mb-0"
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {showPublishConfirmation && (
        <div className="fixed inset-0 overflow-y-auto flex justify-center items-center z-10 card-publish">
          <div className="fixed inset-0">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <div className="bg-white rounded p-8 max-w-md w-11/12 md:w-full mx-auto z-50">
            <p className="text-gray-800 mb-4">
              This will publish all the pages. Are you sure?
            </p>
            <div className="flex justify-end flex-col sm:flex-row">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2 focus:outline-none focus:shadow-outline w-full sm:w-auto mb-2.5 sm:mb-0"
                onClick={() => setShowPublishConfirmation(false)}
              >
                Cancel
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                onClick={onPublishViewClick}
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      )}
      {showRename && (
        <div className="fixed inset-0 overflow-y-auto flex justify-center items-center z-10">
          <div className="fixed inset-0">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <div className="bg-white card-rename rounded p-2 max-w-md w-11/12 md:w-full mx-auto z-50">
            <div className="flex justify-end">
              <CloseOutlinedIcon
                className="hover:cursor-pointer"
                onClick={() => setShowRename(false)}
              />
            </div>
            <div className="flex pt-2 px-4 sm:px-8 pb-4 flex-col justify-between md:flex-row">
              <TextField
                id="outlined-basic"
                // label={renameError !== "" ? renameError : "New website name"}
                label={"New website name"}
                variant="outlined"
                size="small"
                defaultValue={props.data.siteName}
                onChange={(e) => {
                  const sanitizedValue = SanitizeTextboxInput(e.target.value);
                  setRenameError("");
                  setNewSiteName(sanitizedValue);
                }}
                onKeyDown={(e) => {
                  if (["<", ">"].includes(e.key)) {
                    e.preventDefault();
                    setRenameError("Characters < and > are not allowed.");
                  }
                }}
                onBlur={(e) => {
                  const sanitizedValue = SanitizeTextboxInput(e.target.value);
                  setNewSiteName(sanitizedValue);
                }}
                // error={renameError !== ""}
                className="w-full mb-4 md:w-64 md:mb-0"
              />

              <div className="self-center">
                {/* <button
                  className="bg-catelina-blue text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline text-xs w-20 mr-3"
                  onClick={handleDelete}
                >
                  Save
                </button> */}
                <Button
                  variant="contained"
                  size="small"
                  sx={{ height: 44, width: 144 }}
                  className="w-20 mr-2 capitalize"
                  onClick={handleRename}
                >
                  Save
                </Button>
              </div>
            </div>
            {renameError !== "" ? (
              <p className={`text-red-500 pl-3`}>{renameError}</p>
            ) : null}
          </div>
        </div>
      )}
      <img
        onClick={() =>
          window.open(
            `${process.env.EDITOR_ENDPOINT}/${props.data.siteId}`,
            "_self"
          )
        }
        className="w-full siteImageUrl md:min-h-0 md:h-[200px] object-cover cursor-pointer"
        src={props.data.siteImageURL}
      />
      <CardContent>
        <Typography
          gutterBottom
          component="div"
          className="text-base-2"
          display="inline"
        ></Typography>
        {props.data.siteName}
        {props.data.isTemplate === "true" ? " (Template)" : ""}
        {(props.data?.isDirty === "true" ||
          props.data?.disabled === "true") && (
          <>
            <Typography
              display="inline"
              whiteSpace="nowrap"
              sx={{
                fontSize: "0.7rem",
                color: "#fff",
                background: "#f94343",
                borderRadius: "0.75rem",
                overflowWrap: "break-word",
                paddingX: "0.4rem",
                paddingY: "0.06125rem",
                marginLeft: "0.4rem",
              }}
            >
              {props.data.isDirty ? "Expires soon" : "Disabled"}
            </Typography>
          </>
        )}
        <Typography
          sx={{
            fontSize: "0.7rem",
            color: "#243060",
            opacity: props.data.isPublished ? 1 : 0.6,
            overflowWrap: "break-word",
          }}
        >
          {props.data.isPublished ? (
            <Link href={props.data.siteUrl} underline="none" target="_blank">
              {props.data.siteUrl.replace(/^https?:\/\//, "")}
            </Link>
          ) : (
            "Not published"
          )}
        </Typography>
        {props.data.isDirty === "true" && (
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "red",
              marginTop: "6px",
              opacity: props.data.isPublished ? 1 : 0.6,
              overflowWrap: "break-word",
            }}
          >
            <InfoOutlinedIcon fontSize="small" sx={{ marginRight: "6px" }} />
            {calculateTimeRemaining(props.data.createdAt)}
          </Typography>
        )}
      </CardContent>
      <CardActions>
        <div className="w-full flex flex-row justify-between">
          <div className="flex flex-row">
            <Button
              size="small"
              variant="outlined"
              sx={{
                textTransform: "none",
                marginRight: "6px",
                backgroundColor: "white",
              }}
              startIcon={<NoteAltOutlinedIcon />}
              onClick={props.onEditSite}
              // disabled={
              //   props.data.siteId === "2e2b85c5-1b76-4e74-8e39-c82749386c53"
              // }
            >
              Edit site
            </Button>

            <span className="relative setting-icon">
              <img
                onClick={
                  // props.data.siteId === "2e2b85c5-1b76-4e74-8e39-c82749386c53"
                  //   ? null
                  //   :
                  props.onSettingClick
                }
                className="w-[36px] hover:cursor-pointer"
                src={require("../../images/site-setting.svg")}
                alt=""
              />
              <span className="icon-tooltip-text">Site Setting</span>
            </span>
          </div>
          <div className="relative site-menu" onClick={handleClick}>
            <img
              onClick={props.onMoreOptionClick}
              className="w-[36px] hover:cursor-pointer"
              src={require("../../images/extra-option.svg")}
              alt=""
            />
            <span className="icon-tooltip-text">Site Menu</span>
          </div>
        </div>
      </CardActions>

      <Menu
        className="siteMenuOptions"
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {menuItems}
      </Menu>
    </Card>
  );
};

export default SiteItem;
